import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { usePlayer } from "@/context/PlayerContext";
import { usePurchase } from "@/context/PurchaseContext";
import { useAuth } from "@/context/AuthContext";
import { IncludedFormats } from "@/components/IncludedFormats";
import { Play, Pause, ShoppingBag, Loader2, Lock, Clock } from "lucide-react";

const left = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

export default function EarlyAccess() {
  const { id } = useParams();
  const navigate = useNavigate();
  const k = new URLSearchParams(window.location.search).get("k") || "";
  const [track, setTrack] = useState(null);
  const [error, setError] = useState(false);
  const [, tick] = useState(0);
  const { current, isPlaying, playTrack } = usePlayer();
  const { openBuy, loadingId } = usePurchase();
  const { user } = useAuth();
  const [wantBuy, setWantBuy] = useState(false);

  useEffect(() => {
    if (wantBuy && user && t) { setWantBuy(false); openBuy(t); }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const buy = () => { if (!user) setWantBuy(true); openBuy(t); };

  useEffect(() => {
    api.get(`/tracks/${id}`, { params: { k } }).then((r) => setTrack(r.data)).catch(() => setError(true));
    const t = setInterval(() => tick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, [id, k]);

  const t = useMemo(() => track && ({ ...track, early_key: k, stream_url: `/stream/${track.id}?k=${encodeURIComponent(k)}` }), [track, k]);
  const remaining = track?.early_access_until ? left(track.early_access_until) : null;
  const isCurrent = current?.id === track?.id;

  return (
    <div className="min-h-screen grain pb-40">
      <Navbar />
      <div className="relative z-10 max-w-4xl mx-auto px-6 pt-28">
        {error && (
          <div data-testid="early-invalid" className="text-center py-24">
            <Lock className="w-10 h-10 text-[#6b7280] mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-semibold">This early-access link isn't valid</h1>
            <p className="text-[#9ca3af] mt-2">It may have expired or been mistyped.</p>
            <button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#e2ff31] text-black font-semibold px-6 py-3">Browse the store</button>
          </div>
        )}
        {!error && !track && <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#e2ff31]" /></div>}
        {t && (
          <div data-testid="early-access-page" className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-10 items-center">
            <div className="relative aspect-square rounded-2xl overflow-hidden border border-[#2a2a2a] bg-[#1e1e1e]">
              <img src={`${API}${t.cover_url}`} alt={t.title} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="w-full h-full object-cover" />
              <button data-testid="early-play-btn" onClick={() => playTrack(t, [t])} aria-label={`Play ${t.title}`}
                className="absolute bottom-4 left-4 w-14 h-14 rounded-full bg-[#e2ff31] text-black flex items-center justify-center shadow-xl hover:scale-105 transition-transform">
                {isCurrent && isPlaying ? <Pause className="w-6 h-6" fill="currentColor" /> : <Play className="w-6 h-6 ml-0.5" fill="currentColor" />}
              </button>
            </div>
            <div>
              <p data-testid="early-status" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#e2ff31] border border-[#e2ff31]/40 rounded-full px-3 py-1">
                {remaining ? <><Lock className="w-3.5 h-3.5" /> Early access · public in {remaining}</> : <><Clock className="w-3.5 h-3.5" /> Now public</>}
              </p>
              <h1 data-testid="early-title" className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-4">{t.title}</h1>
              <p className="text-[#9ca3af] mt-2">{t.producer}</p>
              <div className="flex flex-wrap items-center gap-3 mt-4 text-xs font-mono text-[#9ca3af]">
                {t.genre && <span className="px-2 py-1 rounded-full bg-[#1e1e1e]">{t.genre}</span>}
                {t.bpm ? <span>{t.bpm} BPM</span> : null}
                {t.music_key ? <span>{t.music_key}</span> : null}
                <IncludedFormats track={t} />
              </div>
              <p className="text-sm text-[#6b7280] mt-6 max-w-md">
                You're seeing this before everyone else because you bought from Co Mann before. It's a one-of-one exclusive — once it's bought, it's gone.
              </p>
              {t.sold ? (
                <p data-testid="early-sold" className="mt-6 text-[#ef4444] font-semibold">This beat has already been sold.</p>
              ) : (
                <button data-testid="early-buy-btn" onClick={buy} disabled={loadingId === t.id}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold px-7 py-3.5 transition-colors">
                  {loadingId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                  Buy first — ${t.price?.toFixed(2)}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
